/*
  <styling-videos> — product page styling videos.
  Thumbnails only download and play while on screen (preload="none" + IntersectionObserver).
  Clicking a thumbnail opens a modal player with previous / next, Esc and arrow keys.
*/
if (!customElements.get('styling-videos')) {
  customElements.define('styling-videos', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('.styling-videos__track');
      this.items = Array.from(this.querySelectorAll('.sv-item'));
      this.prevArrow = this.querySelector('.sv-arrow--prev');
      this.nextArrow = this.querySelector('.sv-arrow--next');
      this.modal = this.querySelector('.sv-modal');
      this.mainVideo = this.querySelector('.sv-modal__video');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.currentIndex = 0;

      if (!this.track || !this.items.length) return;

      // Keep the modal out of transformed/overflow parents so position: fixed covers the viewport.
      if (this.modal) document.body.appendChild(this.modal);

      this.items.forEach((item, index) => item.addEventListener('click', () => this.open(index)));
      this.prevArrow?.addEventListener('click', () => this.scrollBySlide(-1));
      this.nextArrow?.addEventListener('click', () => this.scrollBySlide(1));
      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });

      this.modal?.querySelector('.sv-modal__close')?.addEventListener('click', () => this.close());
      this.modal?.querySelector('.sv-modal__nav--prev')?.addEventListener('click', () => this.step(-1));
      this.modal?.querySelector('.sv-modal__nav--next')?.addEventListener('click', () => this.step(1));
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.close();
      });
      this.onKeydown = (event) => {
        if (!this.modal?.classList.contains('is-open')) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      };
      document.addEventListener('keydown', this.onKeydown);

      this.observeThumbnails();
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this.track);
      this.updateArrows();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.modal?.remove();
      this.initialized = false;
    }

    observeThumbnails() {
      const videos = this.items.map((item) => item.querySelector('video')).filter(Boolean);
      if (this.reduceMotion || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (!video.src && video.dataset.src) video.src = video.dataset.src;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.25 });

      videos.forEach((video) => this.observer.observe(video));
    }

    slideWidth() {
      const slide = this.track.querySelector('.styling-videos__slide');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth;
    }

    scrollBySlide(direction) {
      this.track.scrollBy({ left: direction * this.slideWidth(), behavior: this.reduceMotion ? 'auto' : 'smooth' });
    }

    updateArrows() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      this.toggleAttribute('data-scrollable', maxScroll > 2);
      if (this.prevArrow) this.prevArrow.disabled = this.track.scrollLeft <= 2;
      if (this.nextArrow) this.nextArrow.disabled = this.track.scrollLeft >= maxScroll - 2;
    }

    setVideo(index) {
      const count = this.items.length;
      this.currentIndex = (index + count) % count;
      const item = this.items[this.currentIndex];
      this.mainVideo.src = item.dataset.fullSrc;
      this.mainVideo.poster = item.dataset.poster || '';
      this.mainVideo.currentTime = 0;
      this.mainVideo.play().catch(() => {});
    }

    open(index) {
      if (!this.modal || !this.mainVideo) return;
      this.lastFocused = document.activeElement;
      this.setVideo(index);
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sv-modal-open');
      this.modal.querySelector('.sv-modal__close')?.focus();
    }

    close() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sv-modal-open');
      this.mainVideo.pause();
      this.mainVideo.removeAttribute('src');
      this.mainVideo.load();
      this.lastFocused?.focus();
    }

    step(direction) {
      this.setVideo(this.currentIndex + direction);
    }
  });
}
